import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowRight, Link2, PenTool, Mail, Sparkles, LayoutList, Target, Check, Minus, Palette, Type, Save, Handshake, Wand2, TrendingUp, ShieldCheck, Compass, PlayCircle, Images, QrCode, Globe } from "lucide-react";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import EmailAuthPanel from "@/components/EmailAuthPanel";
import Logo from "@/components/Logo";
import SocialProofStrip from "@/components/SocialProofStrip";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import ProductTourModal from "@/components/ProductTourModal";
import { useLandingViewTracker, trackLandingCtaClick } from "@/lib/landingAnalytics";

const HERO_IMG = "https://images.unsplash.com/photo-1601642964568-1917224f4e4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2FsbSUyMGRlc2slMjBsYXB0b3AlMjBjb2ZmZWV8ZW58MHx8fHwxNzgzODUyMDU3fDA&ixlib=rb-4.1.0&q=85";
const FEAT_IMG = "https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBkb2N1bWVudCUyMHBhcGVyJTIwbWluaW1hbCUyMGRlc2t8ZW58MHx8fHwxNzgzODUyMDYzfDA&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { t } = useT();
  const [landingTourOpen, setLandingTourOpen] = useState(false);
  const [initialClip, setInitialClip] = useState("cv");
  useLandingViewTracker();
  // Deep-link support: `/?tour=<clip>` auto-opens the tour modal at the
  // requested clip so a shared link lands the visitor exactly there.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clip = (params.get("tour") || "").toLowerCase();
    if (["cv", "letter", "interview", "radar"].includes(clip)) {
      setInitialClip(clip);
      setLandingTourOpen(true);
    }
  }, []);
  const handleLogin = () => {
    trackLandingCtaClick("google_login");
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { Icon: Link2, T: t("landFeat1T"), D: t("landFeat1D") },
    { Icon: PenTool, T: t("landFeat2T"), D: t("landFeat2D"), img: FEAT_IMG },
    { Icon: Mail, T: t("landFeat3T"), D: t("landFeat3D") },
    { Icon: Sparkles, T: t("landFeat4T"), D: t("landFeat4D") },
    { Icon: LayoutList, T: t("landFeat5T"), D: t("landFeat5D") },
    { Icon: Handshake, T: t("landFeatRefsT"), D: t("landFeatRefsD") },
  ];

  return (
    <div className="ed-page min-h-screen">
      {/* Sticky glass navbar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/70 border-b border-black/5 px-6 sm:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="login-logo">
          <Logo size="md" showTagline={false} />
        </div>
        <nav className="flex items-center gap-3 sm:gap-6">
          <Link to="/pricing" data-testid="nav-pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline">
            {t("landNavPricing")}
          </Link>
          <Link to="/partner" data-testid="nav-partner" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline">
            {t("landNavPartner")}
          </Link>
          <Link to="/contact" data-testid="nav-contact" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline">
            {t("footerContact")}
          </Link>
          <LanguageToggle testId="login-language-toggle" />
        </nav>
      </header>

      {/* Product-tour banner hidden per user request. */}
      {/*
      <div className="border-t border-b border-neutral-900 bg-neutral-900 text-[#F9F8F6]">
        <button
          type="button"
          data-testid="landing-tour-banner"
          onClick={() => setLandingTourOpen(true)}
          className="w-full max-w-7xl mx-auto px-6 sm:px-10 py-2.5 flex items-center justify-center gap-3 hover:bg-neutral-800 transition-colors group"
        >
          <span className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.2em]">
            <PlayCircle size={14} strokeWidth={1.75} className="text-amber-300" />
            {t("landTourBadge")}
          </span>
          <span className="text-sm font-serif italic hidden sm:inline">
            {t("landTourBannerBody")}
          </span>
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-amber-300 group-hover:text-white transition-colors">
            {t("landTourCta")} →
          </span>
        </button>
      </div>
      */}

      {/* Hero — asymmetrical split */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 sm:pt-16 lg:pt-24 pb-12 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6 land-fade">
          <h1 className="ed-h1" data-testid="landing-h1">
            {t("landH1a")} <span className="serif-italic">{t("landH1accent")}</span>
            <br />
            {t("landH1b")}
          </h1>
          <div className="ed-rule w-24 mt-8" />
          <p className="mt-8 max-w-lg text-neutral-700 leading-relaxed">{t("landSub")}</p>
          <div className="mt-10" id="signup">
            <EmailAuthPanel onGoogleLogin={handleLogin} defaultMode="register" />
          </div>
          <div className="mt-6 flex items-center gap-4">
            <Link
              to="/pricing"
              data-testid="hero-cta-pricing"
              onClick={() => trackLandingCtaClick("hero_pricing")}
              className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              {t("landCtaPricing")} <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
          <p className="mt-4 text-xs text-neutral-500 font-mono">{t("loginDisclaimer")} · {t("priceTrialNote")}</p>
        </div>

        <div className="lg:col-span-6 relative land-fade" style={{ animationDelay: "0.15s" }}>
          <div className="overflow-hidden border border-black/10 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
            <img src={HERO_IMG} alt="" className="w-full h-[26rem] lg:h-[30rem] object-cover land-zoom" />
          </div>
          {/* Floating ledger card */}
          <div className="absolute -bottom-8 -left-4 sm:left-8 bg-white border border-black/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] p-5 w-72" data-testid="hero-float-card">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">{t("landFloatTitle")}</p>
            <div className="mt-3 space-y-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="font-serif text-base leading-tight truncate">Sr. Projectleider</span>
                <span className="ed-badge badge-invited shrink-0">{t("invitedStatus")}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-serif text-base leading-tight truncate">Product Owner</span>
                <span className="ed-badge badge-applied shrink-0">{t("appliedStatus")}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-serif text-base leading-tight truncate">Scrum Master</span>
                <span className="ed-badge badge-rejected shrink-0">{t("rejectedStatus")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prominent trial banner — near the top so visitors don't have to
          scroll past all features before spotting the free-signup CTA. */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 -mt-4 sm:-mt-8">
        <div
          data-testid="landing-trial-banner"
          className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50/70 via-white to-white p-8 sm:p-12 shadow-[0_20px_60px_-30px_rgba(16,185,129,0.35)]"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-100 border border-emerald-300 px-2 py-1">
                <Sparkles size={11} strokeWidth={1.8} /> {t("priceTrialBadge")}
              </span>
              <p className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">{t("priceStartTrialTitle")}</p>
              <p className="text-base text-neutral-700 mt-3 max-w-xl leading-relaxed">
                {t("priceStartTrialSub")}
              </p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-neutral-700 max-w-xl">
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc1")}</li>
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc2")}</li>
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc3")}</li>
                <li className="flex items-center gap-2"><Check size={13} strokeWidth={2} className="text-emerald-600" /> {t("priceTrialInc4")}</li>
              </ul>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
              <button
                data-testid="landing-trial-cta"
                onClick={() => {
                  trackLandingCtaClick("hero_trial_banner");
                  // Scroll straight to the pricing packs so visitors can pick.
                  window.location.href = "/pricing";
                }}
                className="inline-flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 font-mono text-sm uppercase tracking-[0.15em] transition-colors shadow-lg"
              >
                {t("priceStartTrialCta")}
                <span aria-hidden="true">→</span>
              </button>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                {t("priceTrialNoCard")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Multilingual USP banner — highlight the "write in Dutch, apply in any
          language" capability as a differentiator for internationally
          oriented job seekers. */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 mt-6" data-testid="landing-lang-usp">
        <div className="border border-neutral-900 bg-[#F9F8F6] p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1 min-w-0">
            <p className="ed-eyebrow inline-flex items-center gap-2">
              <span className="text-base leading-none" aria-hidden>🌍</span> {t("languageAiSection")}
            </p>
            <p className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">
              {t("langUspTitle")}
            </p>
            <p className="text-sm text-neutral-700 mt-3 max-w-2xl leading-relaxed">
              {t("langUspBody")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {[
              { code: "nl", cc: "nl", label: "Nederlands" },
              { code: "en", cc: "gb", label: "English" },
              { code: "de", cc: "de", label: "Deutsch" },
              { code: "fr", cc: "fr", label: "Français" },
              { code: "es", cc: "es", label: "Español" },
              { code: "pl", cc: "pl", label: "Polski" },
              { code: "tr", cc: "tr", label: "Türkçe" },
              { code: "ar", cc: "ma", label: "العربية" },
            ].map((l) => (
              <div
                key={l.code}
                className="flex flex-col items-center gap-1 px-3 py-2 border border-neutral-200 bg-white"
                data-testid={`landing-lang-usp-${l.code}`}
                title={l.label}
              >
                <img
                  src={`https://flagcdn.com/w80/${l.cc}.png`}
                  alt={l.label}
                  width={36}
                  height={27}
                  loading="lazy"
                  className="object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                />
                <span className="font-mono text-[0.55rem] uppercase tracking-[0.1em] text-neutral-500">{l.code}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof: real aggregate counts + subtle "join thousands" line */}
      <SocialProofStrip />

      {/* Features bento */}
      <section className="border-t border-black/5 bg-[#F0EFEA]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
          <p className="ed-eyebrow">{t("landFeaturesEyebrow")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 max-w-2xl">{t("landFeaturesTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8 sm:mt-12">
            {features.map(({ Icon, T, D, img }, i) => (
              <div
                key={T}
                data-testid={`landing-feature-${i}`}
                className={`bg-white border border-black/5 p-6 sm:p-8 land-fade ${i === 1 ? "md:row-span-2 flex flex-col" : ""}`}
                style={{ animationDelay: `${0.1 + i * 0.08}s` }}
              >
                <Icon size={18} strokeWidth={1.5} />
                <p className="font-serif text-xl sm:text-2xl mt-4 leading-tight">{T}</p>
                <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{D}</p>
                {img && (
                  <div className="mt-6 overflow-hidden border border-black/5 flex-1 min-h-[10rem] hidden md:block">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            ))}

            {/* Featured: Job Search (Adzuna + JSearch) */}
            <div
              data-testid="landing-feature-jobsearch"
              className="md:col-span-3 bg-[#F9F8F6] border border-black/10 p-6 sm:p-10 land-fade grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center"
              style={{ animationDelay: "0.46s" }}
            >
              <div>
                <div className="flex items-center gap-3">
                  <Compass size={18} strokeWidth={1.5} />
                  <span className="rounded-full bg-neutral-900 text-[#F9F8F6] px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                    {t("landJobsEyebrow")}
                  </span>
                </div>
                <p className="font-serif text-2xl sm:text-3xl mt-4 leading-tight">{t("landJobsTitle")}</p>
                <p className="text-sm text-neutral-700 mt-3 leading-relaxed max-w-lg">{t("landJobsBody")}</p>
                <ul className="mt-5 space-y-2">
                  {[t("landJobsBullet1"), t("landJobsBullet2"), t("landJobsBullet3"), t("landJobsBullet4")].map((line) => (
                    <li key={line} className="flex items-start gap-2 text-sm text-neutral-800">
                      <Check size={14} strokeWidth={2} className="mt-1 shrink-0 text-emerald-700" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border border-black/10 p-5" data-testid="jobsearch-demo-card">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">{t("jsSources")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { label: "LinkedIn", color: "#0A66C2" },
                    { label: "Indeed", color: "#003A9B" },
                    { label: "Glassdoor", color: "#0CAA41" },
                    { label: "Nationale Vacaturebank", color: "#E30613" },
                    { label: "Striive", color: "#0E1837" },
                    { label: "Freelance.nl", color: "#F26C3A" },
                    { label: "Jobbird", color: "#F58220" },
                    { label: "Twago", color: "#00A1E0" },
                    { label: "Randstad Freelance", color: "#0033A0" },
                    { label: "Adzuna", color: "#2F80ED" },
                  ].map((s) => (
                    <span
                      key={s.label}
                      className="inline-flex items-center font-mono text-[0.65rem] uppercase tracking-[0.12em] text-white rounded-full px-2.5 py-1"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.label}
                    </span>
                  ))}
                </div>
                <div className="mt-5 space-y-2.5">
                  <div className="flex items-center justify-between gap-3 border border-black/5 bg-[#F0EFEA]/50 px-3 py-2.5">
                    <span className="font-serif text-base leading-tight truncate">Senior React Developer — Amsterdam</span>
                    <span className="shrink-0 font-mono text-[0.55rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5" style={{ backgroundColor: "#0A66C2" }}>LinkedIn</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 border border-black/5 bg-[#F0EFEA]/50 px-3 py-2.5">
                    <span className="font-serif text-base leading-tight truncate">Financieel Adviseur — Rotterdam</span>
                    <span className="shrink-0 font-mono text-[0.55rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5" style={{ backgroundColor: "#E30613" }}>Nationale Vacaturebank</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 border border-black/5 bg-[#F0EFEA]/50 px-3 py-2.5">
                    <span className="font-serif text-base leading-tight truncate">Interim Projectleider — Utrecht</span>
                    <span className="shrink-0 font-mono text-[0.55rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5" style={{ backgroundColor: "#0E1837" }}>Striive</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 border border-black/5 bg-[#F0EFEA]/50 px-3 py-2.5">
                    <span className="font-serif text-base leading-tight truncate">Freelance UX Designer — Remote</span>
                    <span className="shrink-0 font-mono text-[0.55rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5" style={{ backgroundColor: "#F26C3A" }}>Freelance.nl</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Featured: Vacature Criteria Match */}
            <div
              data-testid="landing-feature-match"
              className="md:col-span-3 bg-[#24211D] text-[#F9F8F6] p-6 sm:p-10 land-fade grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center"
              style={{ animationDelay: "0.5s" }}
            >
              <div>
                <div className="flex items-center gap-3">
                  <Target size={18} strokeWidth={1.5} />
                  <span className="rounded-full bg-amber-300 text-[#1A1A1A] px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                    {t("landFeat6Badge")}
                  </span>
                </div>
                <p className="font-serif text-xl sm:text-2xl md:text-3xl mt-4 leading-tight">{t("landFeat6T")}</p>
                <p className="text-sm text-neutral-300 mt-3 leading-relaxed max-w-lg">{t("landFeat6D")}</p>
              </div>
              <div className="hidden md:block bg-[#F9F8F6] text-[#1A1A1A] border border-black/10 p-5" data-testid="match-demo-card">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">{t("landFeat6Match")}</p>
                <div className="mt-3 space-y-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-serif text-base leading-tight truncate">5+ jaar projectervaring</span>
                    <span className="shrink-0 inline-flex items-center gap-1 text-emerald-700 font-mono text-[0.65rem] uppercase tracking-[0.1em]"><Check size={12} strokeWidth={2.5} /> Sterk</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-serif text-base leading-tight truncate">Agile / Scrum-certificering</span>
                    <span className="shrink-0 inline-flex items-center gap-1 text-emerald-700 font-mono text-[0.65rem] uppercase tracking-[0.1em]"><Check size={12} strokeWidth={2.5} /> Sterk</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-serif text-base leading-tight truncate">Ervaring met overheid</span>
                    <span className="shrink-0 inline-flex items-center gap-1 text-amber-700 font-mono text-[0.65rem] uppercase tracking-[0.1em]"><Minus size={12} strokeWidth={2.5} /> Uitlichten</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Featured: Design your own Template */}
            <div
              data-testid="landing-feature-wizard"
              className="md:col-span-3 bg-[#F9F8F6] border border-black/10 p-6 sm:p-10 land-fade grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center"
              style={{ animationDelay: "0.54s" }}
            >
              <div>
                <div className="flex items-center gap-3">
                  <Wand2 size={18} strokeWidth={1.5} />
                  <span className="rounded-full bg-emerald-600 text-white px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                    {t("landFeatWizardBadge")}
                  </span>
                </div>
                <p className="font-serif text-xl sm:text-2xl md:text-3xl mt-4 leading-tight">{t("landFeatWizardT")}</p>
                <p className="text-sm text-neutral-700 mt-3 leading-relaxed max-w-lg">{t("landFeatWizardD")}</p>
                <p className="text-xs text-neutral-500 mt-4 italic font-serif leading-relaxed max-w-lg hidden md:block">
                  {t("landFeatWizardExample")}
                </p>
              </div>
              <div className="hidden md:block bg-white border border-black/10 p-5" data-testid="wizard-demo-card">
                <div className="flex items-center gap-4">
                  <div className="inline-flex items-center gap-1 border border-amber-600 bg-amber-50 text-amber-800 px-2.5 py-1 font-mono text-[0.7rem]">
                    <Target size={12} strokeWidth={1.75} /> 62%
                  </div>
                  <TrendingUp size={18} strokeWidth={1.5} className="text-emerald-700 shrink-0" />
                  <div className="inline-flex items-center gap-1 border border-emerald-700 bg-emerald-50 text-emerald-800 px-2.5 py-1 font-mono text-[0.7rem]">
                    <Target size={12} strokeWidth={1.75} /> 89%
                  </div>
                  <span className="ml-auto font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-emerald-700 bg-emerald-50 text-emerald-800 px-2 py-0.5">+27</span>
                </div>
                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="ed-num shrink-0">01</span>
                    <span className="font-serif truncate">{t("landFeatWizardStep1")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="ed-num shrink-0">02</span>
                    <span className="font-serif truncate">{t("landFeatWizardStep2")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="ed-num shrink-0">03</span>
                    <span className="font-serif truncate">{t("landFeatWizardStep3")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="ed-num shrink-0">04</span>
                    <span className="font-serif truncate">{t("landFeatWizardStep4")}</span>
                  </div>
                </div>
                <div className="mt-4 border-t border-neutral-200 pt-3 flex items-start gap-2">
                  <ShieldCheck size={14} strokeWidth={1.75} className="shrink-0 text-emerald-700 mt-0.5" />
                  <p className="text-xs text-neutral-600 leading-relaxed">{t("landFeatWizardGuard")}</p>
                </div>
              </div>
            </div>

            {/* Featured: Design your own Template */}
            <div
              data-testid="landing-feature-template"
              className="md:col-span-3 bg-[#F9F8F6] border border-black/10 p-6 sm:p-10 land-fade grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center"
              style={{ animationDelay: "0.58s" }}
            >
              <div className="order-2 lg:order-1 bg-white border border-black/10 p-5 hidden md:block" data-testid="template-demo-card">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">3 stappen</p>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-3 border border-black/5 bg-[#F0EFEA]/60 px-3 py-2.5">
                    <span className="ed-num shrink-0">01</span>
                    <LayoutList size={14} strokeWidth={1.5} className="shrink-0" />
                    <span className="font-serif text-base leading-tight truncate">{t("landFeat7Step1")}</span>
                  </div>
                  <div className="flex items-center gap-3 border border-black/5 bg-[#F0EFEA]/60 px-3 py-2.5">
                    <span className="ed-num shrink-0">02</span>
                    <Palette size={14} strokeWidth={1.5} className="shrink-0" />
                    <Type size={14} strokeWidth={1.5} className="shrink-0 -ml-1" />
                    <span className="font-serif text-base leading-tight truncate">{t("landFeat7Step2")}</span>
                  </div>
                  <div className="flex items-center gap-3 border border-black/5 bg-[#F0EFEA]/60 px-3 py-2.5">
                    <span className="ed-num shrink-0">03</span>
                    <Save size={14} strokeWidth={1.5} className="shrink-0" />
                    <span className="font-serif text-base leading-tight truncate">{t("landFeat7Step3")}</span>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3">
                  <Palette size={18} strokeWidth={1.5} />
                  <span className="rounded-full bg-[#24211D] text-[#F9F8F6] px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                    {t("landFeat7Badge")}
                  </span>
                </div>
                <p className="font-serif text-2xl sm:text-3xl mt-4 leading-tight">{t("landFeat7T")}</p>
                <p className="text-sm text-neutral-600 mt-3 leading-relaxed max-w-lg">{t("landFeat7D")}</p>
              </div>
            </div>

            {/* Featured: Portfolio module — Playwright website crawler,
                multi-image collages, AI cover generator (Nano Banana),
                magazine-style PDF export and a public share link with a
                language switcher. This is a JobGrid-specific super-power
                that competitors don't have, so it earns its own dark
                bento tile below the CV wizard. */}
            <div
              data-testid="landing-feature-portfolio"
              className="md:col-span-3 bg-[#0F0F10] text-[#F9F8F6] p-6 sm:p-10 land-fade grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center"
              style={{ animationDelay: "0.62s" }}
            >
              <div>
                <div className="flex items-center gap-3">
                  <Images size={18} strokeWidth={1.5} className="text-[#D9A441]" />
                  <span className="rounded-full bg-[#D9A441] text-[#1A1A1A] px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                    {t("landFeatPortfolioBadge")}
                  </span>
                </div>
                <p className="font-serif text-2xl sm:text-3xl md:text-4xl mt-4 leading-tight">{t("landFeatPortfolioT")}</p>
                <p className="text-sm text-neutral-300 mt-3 leading-relaxed max-w-lg">{t("landFeatPortfolioD")}</p>
                <ul className="mt-5 space-y-2 max-w-lg">
                  {["landFeatPortfolioBullet1", "landFeatPortfolioBullet2", "landFeatPortfolioBullet3", "landFeatPortfolioBullet4"].map((k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-neutral-200">
                      <Check size={14} strokeWidth={2} className="mt-1 shrink-0 text-[#D9A441]" />
                      <span>{t(k)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Magazine-style demo card — mimics the actual public
                  portfolio look with a mini "cover" (gradient block),
                  a project mosaic, and a QR badge so visitors get the
                  vibe of what they'll ship. */}
              <div className="hidden md:block" data-testid="portfolio-demo-card">
                <div className="bg-[#F9F8F6] text-[#1A1A1A] border border-white/10 shadow-[0_20px_60px_-30px_rgba(217,164,65,0.35)] overflow-hidden">
                  {/* Faux cover — warm gradient with a small eyebrow to
                      echo the real magazine cover produced by the app. */}
                  <div className="relative h-28 sm:h-36 bg-gradient-to-br from-[#3B2F26] via-[#7D5A3A] to-[#D9A441] flex items-end p-4">
                    <p className="font-mono text-[0.55rem] uppercase tracking-[0.2em] text-[#F9F8F6]/80">Portfolio</p>
                    <p className="absolute right-4 top-4 font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-white/15 text-white px-2 py-0.5 backdrop-blur">
                      AI cover
                    </p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-[0.65rem] font-mono uppercase tracking-[0.15em] text-neutral-500">
                      <span>Project 01</span>
                      <span className="text-neutral-400">·</span>
                      <Globe size={11} strokeWidth={1.5} /> jouwsite.nl
                    </div>
                    <p className="font-serif text-lg leading-tight">Rebrand & product-launch</p>
                    {/* Mini collage — 4 gradient tiles as image
                        placeholders in the layout the real collage uses. */}
                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="col-span-2 row-span-2 h-16 bg-gradient-to-br from-neutral-800 to-neutral-500" />
                      <div className="h-[30px] bg-gradient-to-br from-amber-200 to-amber-500" />
                      <div className="h-[30px] bg-gradient-to-br from-emerald-200 to-emerald-600" />
                      <div className="h-[30px] bg-gradient-to-br from-rose-200 to-rose-500" />
                      <div className="h-[30px] bg-gradient-to-br from-sky-200 to-sky-600" />
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <p className="text-xs text-neutral-500 font-mono uppercase tracking-[0.15em]">
                        Verantwoordelijkheden ↓
                      </p>
                      <span className="inline-flex items-center gap-1 font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-600 border border-neutral-300 px-2 py-0.5">
                        <QrCode size={10} strokeWidth={1.5} /> QR
                      </span>
                    </div>
                  </div>
                </div>
                <p className="mt-3 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-neutral-400">
                  {t("landFeatPortfolioExport")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser — mirrors the /pricing packs (one-time payment).
          Shows the entry-price pack + best-value pack side by side. */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 py-14 sm:py-24">
        <p className="ed-eyebrow">{t("priceEyebrow")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-4">{t("landPricingTitle")}</h2>
        <p className="text-base text-neutral-700 mt-3 max-w-2xl leading-relaxed">{t("landPricingSub")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8 sm:mt-10">
          <div className="bg-white border border-black/10 p-6 sm:p-8" data-testid="teaser-entry">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">{t("landTeaserEntryEyebrow")}</p>
            <p className="mt-4 flex items-baseline gap-2 whitespace-nowrap">
              <span className="font-serif text-4xl sm:text-5xl">€7,50</span>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-500">{t("priceOneTime")}</span>
            </p>
            <p className="font-mono text-[0.65rem] text-neutral-500 mt-2">{t("landTeaserEntryDuration")}</p>
          </div>
          <div className="relative bg-[#24211D] text-[#F9F8F6] p-6 sm:p-8" data-testid="teaser-best">
            {/* Yellow ribbon top-right for "Meest gekozen" — a corner banner
                that pops against the dark card without competing with the
                pack price. */}
            <div className="absolute -top-3 right-6 bg-amber-400 text-neutral-900 px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] shadow-md">
              {t("priceBestValue")}
            </div>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-amber-200/90">{t("landTeaserBestEyebrow")}</p>
            <p className="mt-4 flex items-baseline gap-2 whitespace-nowrap">
              <span className="font-serif text-4xl sm:text-5xl">€75</span>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-300">{t("priceOneTime")}</span>
            </p>
            <p className="font-mono text-[0.65rem] text-neutral-300 mt-2">{t("landTeaserBestDuration")}</p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link to="/pricing" data-testid="teaser-cta" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors">
            {t("landPricingCta")} <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">
            {t("priceTrialNoCard")}
          </span>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/5 px-6 sm:px-10 py-10 sm:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <Logo size="md" showTagline={false} />
            <p className="text-sm text-neutral-500 mt-2 max-w-xs leading-relaxed">{t("landFooterTag")}</p>
          </div>
          <div data-testid="footer-company">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mb-3">{t("footerCompany")}</p>
            <p className="font-serif text-base">CHEPL bv</p>
            <p className="text-sm text-neutral-600 mt-1">
              <a href="mailto:info@jobgrid.nl" className="hover:underline underline-offset-2">info@jobgrid.nl</a>
            </p>
            <p className="text-sm text-neutral-600">
              <a href="tel:+31302072501" className="hover:underline underline-offset-2">030 207 2501</a>
            </p>
            <p className="font-mono text-[0.65rem] text-neutral-500 mt-2">KvK 42087917 · BTW NL869653817B01</p>
          </div>
          <nav className="flex flex-col sm:items-end gap-3">
            <Link to="/pricing" data-testid="footer-pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors">
              {t("landFooterPricing")}
            </Link>
            <Link to="/partner" data-testid="footer-partner" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors">
              {t("landNavPartner")}
            </Link>
            <Link to="/contact" data-testid="footer-contact" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors">
              {t("footerContact")}
            </Link>
            <Link to="/admin/login" data-testid="footer-admin" className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors">
              {t("landFooterAdmin")}
            </Link>
          </nav>
        </div>
      </footer>
      <MobileStickyCTA onStart={() => { window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      <ProductTourModal
        open={landingTourOpen}
        onClose={() => setLandingTourOpen(false)}
        initialClip={initialClip}
        source="landing"
      />
    </div>
  );
}
